import { ref, onMounted, computed, onBeforeUnmount } from 'vue';
import { getMaintenanceServiceAsync } from './Maintenance.facade';
import { MaintenanceStatus } from './Maintenance.types';
import { logger } from '@/common/api/logger';
import { parseErrorMessage } from '@/common/error';

export function useMaintenanceStatus(enabled: boolean) {
  if (!enabled) {
    // Don't setup any maintenance checks, if it's disabled.
    return computed<MaintenanceStatus['phase']>(() => 'none');
  }

  const status = ref<MaintenanceStatus['phase']>('none');
  let clearSubscription: (() => void) | null = null;
  let isUnmounted = false;

  onBeforeUnmount(() => {
    isUnmounted = true;
    clearSubscription?.();
  });

  onMounted(async function setupMaintenanceGetter(times = 3) {
    try {
      const maintenanceService = await getMaintenanceServiceAsync();
      status.value = maintenanceService.getCurrentStatus().phase;

      clearSubscription = maintenanceService.subscribe((newStatus) => {
        status.value = newStatus.phase;
      });
    } catch (error) {
      // Sometimes, if the service is not yet initialized, we might fail to get the status.
      // If this service was not disabled, then we should check maintenance status again later,
      // so we can just assume it's not in maintenance for now.
      logger.error(
        'Failed to get maintenance status, possibly because the maintenance service is not initialized. Assuming no maintenance.',
        { error: parseErrorMessage(error) }
      );
      status.value = 'none';
      // Wait for sometime and check again.
      await new Promise((resolve) => setTimeout(resolve, 2000));
      if (!isUnmounted && times > 0) setupMaintenanceGetter(times - 1);
    }
  });

  return computed(() => status.value);
}
